import React, { useState, useEffect } from 'react';
import { supabaseAdmin } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { SECTIONS } from '../services/auditLog';
import './AuditLog.css';

const ACTION_LABELS = {
  create: 'Utworzenie',
  update: 'Edycja',
  delete: 'Usunięcie',
  publish: 'Publikacja',
  status_change: 'Zmiana statusu',
  login: 'Logowanie',
};

function AuditLogPage() {
  const { canAccessRoute } = useAuth();
  const canViewAuditLog = canAccessRoute('/audit-log');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectionFilter, setSectionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [userList, setUserList] = useState([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 50;

  useEffect(() => {
    if (canViewAuditLog) loadUserList();
  }, [canViewAuditLog]);

  useEffect(() => {
    if (canViewAuditLog) loadLogs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewAuditLog, page, sectionFilter, dateFrom, dateTo, userFilter]);

  const loadUserList = async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('admin_users')
        .select('id, name, email')
        .order('name', { ascending: true, nullsFirst: false });
      if (error) throw error;
      setUserList(data || []);
    } catch (err) {
      console.error('Error loading users for filter:', err);
      setUserList([]);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      let query = supabaseAdmin
        .from('admin_audit_log')
        .select('*', { count: 'exact' })
        .neq('action', 'login')
        .order('created_at', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);

      if (sectionFilter) query = query.eq('section', sectionFilter);
      if (dateFrom) query = query.gte('created_at', `${dateFrom}T00:00:00.000Z`);
      if (dateTo) query = query.lte('created_at', `${dateTo}T23:59:59.999Z`);
      if (userFilter) query = query.eq('user_id', userFilter);

      const { data, error, count } = await query;
      if (error) throw error;
      setLogs(data || []);
      setTotalCount(count ?? 0);
    } catch (error) {
      console.error('Error loading audit log:', error);
      toast.error('Błąd podczas ładowania dziennika zdarzeń');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '–';
    const d = new Date(iso);
    return d.toLocaleString('pl-PL', {
      dateStyle: 'short',
      timeStyle: 'medium',
    });
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const sectionsList = Object.values(SECTIONS).filter(Boolean).filter(s => s !== SECTIONS.LOGIN);

  if (!canViewAuditLog) {
    return null;
  }

  return (
    <div className="audit-log-page">
      <div className="page-header">
        <h2>Dziennik zdarzeń</h2>
      </div>
      <p className="audit-log-description">
        Zdarzenia z panelu administratora. Widoczny tylko dla administratorów.
      </p>

      <div className="audit-log-filters card">
        <div className="filters-row">
          <div className="filter-group">
            <label className="form-label">Zakładka</label>
            <select
              className="form-input"
              value={sectionFilter}
              onChange={(e) => { setSectionFilter(e.target.value); setPage(0); }}
            >
              <option value="">Wszystkie</option>
              {sectionsList.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label className="form-label">Data od</label>
            <input
              type="date"
              className="form-input"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
            />
          </div>
          <div className="filter-group">
            <label className="form-label">Data do</label>
            <input
              type="date"
              className="form-input"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
            />
          </div>
          <div className="filter-group">
            <label className="form-label">Użytkownik</label>
            <select
              className="form-input"
              value={userFilter}
              onChange={(e) => { setUserFilter(e.target.value); setPage(0); }}
            >
              <option value="">Wszyscy</option>
              {userList.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name ? `${u.name} (${u.email})` : u.email}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="card audit-log-table-wrap">
        {loading ? (
          <div className="loading">Ładowanie...</div>
        ) : logs.length === 0 ? (
          <div className="empty-state">Brak wpisów w dzienniku dla wybranych filtrów.</div>
        ) : (
          <>
            <table className="table audit-log-table">
              <thead>
                <tr>
                  <th>Data i godzina</th>
                  <th>Kto</th>
                  <th>Zakładka</th>
                  <th>Operacja</th>
                  <th>Encja / etykieta</th>
                  <th>Opis zmiany</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td className="audit-date">{formatDate(log.created_at)}</td>
                    <td>
                      <span className="audit-user">{log.user_name || log.user_email || '–'}</span>
                      {log.user_email && log.user_name && (
                        <span className="audit-email"> ({log.user_email})</span>
                      )}
                    </td>
                    <td>{log.section || '–'}</td>
                    <td>{ACTION_LABELS[log.action] || log.action}</td>
                    <td>
                      {log.entity_label ? (
                        <span title={log.entity_id}>{log.entity_label}</span>
                      ) : (
                        log.entity_type ? `${log.entity_type}${log.entity_id ? ` #${log.entity_id}` : ''}` : '–'
                      )}
                    </td>
                    <td className="audit-details">{log.details || '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="audit-log-pagination">
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ← Wstecz
                </button>
                <span className="pagination-info">
                  Strona {page + 1} z {totalPages} (łącznie {totalCount} wpisów)
                </span>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Dalej →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function AuditLog() {
  return <AuditLogPage />;
}
